// Resolves the post-login redirect target (`?next=` param / iframe
// loggedInRedirect). Absolute URLs are only honored for allowlisted origins —
// this allowlist is the open-redirect defense, so no wildcards and no
// pattern-matching. Everything else absolute is dropped; relative paths stay
// in-app via history.push.

export const EXTERNAL_REDIRECT_ALLOWLIST: ReadonlyArray<string> = [
  "https://developer.allstar.gg",
];

export type TLoginRedirect =
  | { kind: "external"; url: string }
  | { kind: "internal"; path: string }
  | null;

/**
 * Resolves and performs the post-login navigation. Returns true if it
 * navigated; false when the target was missing or dropped by the allowlist,
 * so callers can apply their own fallback (or none).
 */
export const followLoginRedirect = (
  target: string | null | undefined,
  push: (path: string) => void,
): boolean => {
  const resolved = resolveLoginRedirect(target);
  if (!resolved) return false;

  if (resolved.kind === "external") {
    window.location.assign(resolved.url);
  } else {
    push(resolved.path);
  }
  return true;
};

export const resolveLoginRedirect = (
  target: string | null | undefined,
): TLoginRedirect => {
  if (!target) return null;

  let parsed: URL | null = null;
  try {
    parsed = new URL(target);
  } catch {
    parsed = null;
  }

  if (parsed) {
    // Covers javascript:, data:, http-downgrade, and foreign origins — only an
    // exact allowlisted origin survives.
    if (!EXTERNAL_REDIRECT_ALLOWLIST.includes(parsed.origin)) return null;
    return { kind: "external", url: parsed.toString() };
  }

  // Protocol-relative URLs ("//evil.example") don't parse as absolute but
  // would still leave the site — drop them.
  if (target.startsWith("//")) return null;

  return { kind: "internal", path: target };
};
